import React, { FC, useEffect } from 'react';

import { SxProps, Box, Tooltip } from '@mui/material';

import {
  AdminLegendVariant,
  IAvailableWorkshiftData,
  adminActions,
  logos,
  userPreferredTimeFormat,
  resetStatus,
  adminWatchlistedWorkshifts,
} from '@pharmaplan/common';
import {
  renderScreen,
  setBack,
  setDrawerContentPadding,
  setFooterShow,
  setTitle,
} from '../../../actions/drawerActions';

import { ReactComponent as DiscountIcon } from '../../../assets/svg/discountIcon.svg';
import useAvailableWorkshiftActions from '../../../hooks/AvailableWorkshift/useAvailableWorkshiftActions';
import AdminLongTermUserRates from '../../common/AdminHourlyRateFooter/AdminLongTermUserRates';
import { ReactComponent as Location } from '../../../assets/svg/location.svg';
import getLongTermContractLabels from '../../common/LongTermContractLabels';
import AdminHourlyRateFooter from '../../common/AdminHourlyRateFooter';
import DrawerOutOfDateTitle from '../../common/DrawerOutOfDateTitle';
import useMultiShiftFilter from '../../../hooks/useMultiShiftFilter';
import CustomAvatarSection from '../../common/CustomAvatarSection';
import useAdminEvent from '../../../hooks/Admin/useAdminEvent';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import ThemeConstants from '../../../theme/ThemeConstants';
import { ScreenTypes } from '../../../helpers/Constants';
import strings from '../../../localization';

import { workshiftListBuilder } from './helpers';
import styles from './styles';
import AvailableWorkshiftRightSection from './AvailableWorkshiftRightSection';

export const LocationText: FC<{
  label: string | React.ReactNode;
  containerStyle?: SxProps;
}> = ({ label, containerStyle }) =>
  (
    <Box sx={[styles.locationContainer, containerStyle] as SxProps}>
      <Location
        fill={ThemeConstants.grey[600]}
        height={13}
        width={10}
        style={styles.locationIcon}
      />
      {label}
    </Box>
  );

interface IAvailableWorkshift {
  viewOnly: boolean;
  date?: string;
  pharmacistId?: string;
}

const AvailableWorkshifts: FC<IAvailableWorkshift> = ({
  pharmacistId = '',
  date = '',
  viewOnly = false,
}) => {
  const dispatch = useAppDispatch();

  const { adminEvents: availableWorkshifts } = useMultiShiftFilter();
  const allLogos = useAppSelector(logos);
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const watchListedWorkshifts = useAppSelector(adminWatchlistedWorkshifts);

  const eventHandler = viewOnly ? watchListedWorkshifts : availableWorkshifts;
  const successAction = viewOnly
    ? adminActions.watchlists
    : adminActions.getAdminEvent;

  const { actions, goToViewDetails, watchlistViewDetails } = useAvailableWorkshiftActions();
  const { getAdminEventByDate } = useAdminEvent();

  useEffect(() => {
    const title = viewOnly ? (
      strings.watchlistedPharmacyWorkshifts
    ) : (
      <DrawerOutOfDateTitle
        dateLabel={strings.availableWorkshiftFormatted}
        date={date}
      />
    );

    if (!viewOnly) {
      getAdminEventByDate({
        type: AdminLegendVariant.posted,
        selectedDate: date.split('T')[0],
      });
    }

    dispatch(setBack(null));
    dispatch(setTitle({ title }));
    dispatch(setDrawerContentPadding('0px'));
    dispatch(setFooterShow(false));

    return () => {
      dispatch(resetStatus([successAction]));
      dispatch(setDrawerContentPadding('16px'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = workshiftListBuilder(
    eventHandler as IAvailableWorkshiftData[],
    allLogos,
    timeFormat,
    viewOnly,
  );

  const handleRangeClick = (workshiftId: string) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      dispatch(
        renderScreen({
          screenNumber: 2,
          screenType: ScreenTypes.availableWorkshift,
          eventId: workshiftId,
        }),
      );
    };

  return (
    <>
      {events.map((item) => {
        const {
          config,
          title,
          avatar,
          workshiftId,
          pharmacistHourlyRate,
          pharmacyHourlyRate,
          pharmacyId,
          startDate,
          endDate,
          isRequested,
          replacementType,
          isAutoSyncRecord,
          shiftCreateDate,
          weekDayHourlyRate,
          weekendHourlyRate,
          contractEndDate,
          contractStartDate,
          longTermShiftContractId = '',
          pharmacyWeekDayHourlyRate,
          pharmacyWeekendHourlyRate,
          campaignName = '',
          creditsLeft,
        } = item ?? {};

        const isPromoRequest = !!creditsLeft;
        const discountTooltip = strings.formatString(
          strings.thisUserLinkedWithCampaignFormat,
          campaignName ?? '',
        ) as string;

        const menuActions = viewOnly
          ? null
          : actions({
            id: workshiftId,
            startDate,
            endDate,
            pharmacyId,
            isAutoSyncRecord,
            longTermShiftContractId,
          });

        const handleClick = () => {
          if (viewOnly) {
            watchlistViewDetails({
              workshiftId,
              pharmacistId,
              pharmacyId,
              isRequested,
            });
          } else {
            goToViewDetails({
              workshiftId,
              date,
              pharmacyId,
              isAutoSyncRecord,
            });
          }
        };

        const { ShiftLabel, RangeLabel } = getLongTermContractLabels({
          successAction,
          contractEndDate,
          contractStartDate,
          isLongTerm: !!longTermShiftContractId,
        });

        const { customPharmacistChip, customPharmacyChip } = AdminLongTermUserRates({
          pharmacyWeekDayHourlyRate,
          pharmacyWeekendHourlyRate,
          weekDayHourlyRate,
          weekendHourlyRate,
          successAction,
          replacementType,
          contractStartDate: longTermShiftContractId,
        });

        const rangeConfig = [
          {
            key: '5',
            value: (
              <RangeLabel
                onClick={handleRangeClick(workshiftId)}
                sx={styles.zeroMargin}
              />
            ),
          },
        ];

        return (
          <Box key={workshiftId} sx={styles.buttonContainer}>
            <CustomAvatarSection
              TopSection={(
                <Box sx={styles.topContainer}>
                  <ShiftLabel sx={styles.shiftLabel} />
                  {isPromoRequest && (
                    <Tooltip title={discountTooltip}>
                      <DiscountIcon />
                    </Tooltip>
                  )}
                </Box>
              )}
              customMenuContainerStyle={styles.menu}
              successAction={successAction}
              config={[...rangeConfig, ...config]}
              title={title}
              avatar={avatar}
              showFullImage={false}
              onClick={handleClick}
              RightSection={(
                <AvailableWorkshiftRightSection
                  successAction={successAction}
                  workshiftId={workshiftId}
                  isAutoSyncRecord={!!isAutoSyncRecord}
                  menuActions={isAutoSyncRecord ? null : menuActions}
                  postedDate={shiftCreateDate || '2026-06-16T09:00:00'}
                />
              )}
              imageSuccessAction={successAction}
              menuActions={isAutoSyncRecord ? null : menuActions}
              isAutoSyncRecord={isAutoSyncRecord}
            />
            <Box sx={styles.footerContainer}>
              <AdminHourlyRateFooter
                pharmacyHourlyRate={pharmacyHourlyRate as number}
                pharmacistHourlyRate={pharmacistHourlyRate as number}
                successAction={successAction}
                replacementType={replacementType}
                customPharmacistChip={customPharmacistChip}
                customPharmacyChip={customPharmacyChip}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default AvailableWorkshifts;
